import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation, Outlet, useParams } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/i18n";
import { AuthProvider, clearUserStorage, useAuth } from "@/auth";
import api from "@/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import HowItWorks from "@/pages/HowItWorks";
import Business from "@/pages/Business";
import Compatibility from "@/pages/Compatibility";
import SimsConsole from "@/pages/SimsConsole";
import AppDownload from "@/pages/AppDownload";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import TopupPage from "@/pages/TopupPage";
import LegalPage from "@/pages/LegalPage";
import ServiceCharter from "@/pages/ServiceCharter";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import IdentityUpload from "@/pages/IdentityUpload";
import InstallAppPopup from "@/components/InstallAppPopup";
import IdentityRedoAlert from "@/components/IdentityRedoAlert";
import CookieBanner from "@/components/CookieBanner";
import SupportChat from "@/components/SupportChat";
import GoogleOneTap from "@/components/GoogleOneTap";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Reveal } from "@/components/Reveal";
import AccessibilityWidget from "@/components/AccessibilityWidget";

function AuthCallback() {
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    (async () => {
      let isAdmin = false;
      try {
        const { data } = await api.post("/auth/google-session", { session_id: sessionId, referral_code: localStorage.getItem("atlas_ref") || undefined });
        clearUserStorage(); // azzera l'utenza precedente prima di stabilire la nuova sessione
        localStorage.removeItem("atlas_ref");
        isAdmin = !!data?.is_admin;
        setUser(data);
      } catch {
        setUser(null);
      }
      const next = new URLSearchParams(window.location.search).get("next");
      // admin: direttamente in dashboard, mai nell'area cliente pubblica
      window.location.replace(next || (isAdmin ? "/admin" : "/account"));
    })();
  }, [setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]" data-testid="auth-callback-loading">
      <div className="font-mono-t text-xs uppercase tracking-[0.3em] text-[var(--text-muted)] animate-pulse">SIMAMMA</div>
    </div>
  );
}

function GoogleCallback() {
  // Ritorno dall'OAuth Google diretto: scambia il code col backend (che lo converte in
  // sessione Simamma) e porta alla destinazione. CSRF: verifica dello state generato al click.
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      try {
        const q = new URLSearchParams(window.location.search);
        if (!q.get("code") || q.get("state") !== sessionStorage.getItem("gstate")) {
          throw new Error("state_mismatch");
        }
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const { data } = await api.post("/auth/google/code", {
          code: q.get("code"),
          redirect_uri: window.location.origin + "/auth/google/callback",
          referral_code: localStorage.getItem("atlas_ref") || undefined,
          lang: (navigator.language || "en").slice(0, 2),
        });
        clearUserStorage(); // azzera l'utenza precedente prima di stabilire la nuova sessione
        localStorage.removeItem("atlas_ref");
        localStorage.setItem("atlas_token", data.token);
        setUser(data.user);
        const next = sessionStorage.getItem("gnext");
        sessionStorage.removeItem("gstate");
        sessionStorage.removeItem("gnext");
        window.location.replace(next || (data.user?.is_admin ? "/admin" : "/account"));
      } catch {
        sessionStorage.removeItem("gstate");
        window.location.replace("/account");
      }
    })();
  }, [setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]" data-testid="google-callback-loading">
      <div className="font-mono-t text-xs uppercase tracking-[0.3em] text-[var(--text-muted)] animate-pulse">SIMAMMA</div>
    </div>
  );
}

function ReferralLanding() {
  // Link personale /r/{code} (condiviso anche dall'app): pagina di benvenuto vera,
  // con beneficio dell'invito, CTA e link legali in vista. Il codice viene salvato
  // e agganciato all'account alla registrazione (email, Google o Apple).
  const { code } = useParams();
  const { t } = useLanguage();
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    if (code) localStorage.setItem("atlas_ref", code.toLowerCase());
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, [code]);
  const friendPct = Math.round(cfg?.referral?.friend_pct ?? 10);
  const refPct = Math.round(cfg?.referral?.pct ?? 10);
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="referral-landing">
      <Reveal>
        <div className="max-w-lg w-full rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-8 sm:p-12 text-center">
          <div className="font-mono-t text-[10px] uppercase tracking-[0.3em] text-[var(--accent-terracotta)] mb-4">
            {t("refl.eyebrow")}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-[var(--text-primary)]">
            {t("refl.title")}
          </h1>
          <div className="my-6 inline-flex items-center justify-center rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 px-8 py-3">
            <span className="font-display font-black text-3xl text-[#E8551F]" data-testid="referral-friend-pct">-{friendPct}%</span>
            <span className="ml-3 text-sm text-slate-600 text-left leading-tight">{t("refl.discountNote")}</span>
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {t("refl.sub").replace("{pct}", refPct)}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/account" data-testid="referral-cta-account"
              className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-8 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
              {t("refl.ctaAccount")}
            </Link>
            <Link to="/destinations" data-testid="referral-cta-explore"
              className="rounded-full border border-[var(--border-strong)] px-8 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
              {t("refl.ctaExplore")}
            </Link>
          </div>
          <p className="mt-8 text-[11px] text-[var(--text-muted)] leading-relaxed" data-testid="referral-legal-note">
            {t("refl.legalNote")}{" "}
            <Link to="/legal/terms" className="text-[#FF6B35] hover:underline">{t("legal.terms")}</Link>
            {" · "}
            <Link to="/legal/privacy" className="text-[#FF6B35] hover:underline">{t("legal.privacy")}</Link>
            {" · "}
            <Link to="/legal/cookies" className="text-[#FF6B35] hover:underline">{t("legal.cookies")}</Link>
            {" · "}
            <Link to="/legal/refunds" className="text-[#FF6B35] hover:underline">{t("legal.refunds")}</Link>
          </p>
        </div>
      </Reveal>
    </div>
  );
}

function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Nav />
      <IdentityRedoAlert />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <InstallAppPopup />
      <SupportChat />
      <CookieBanner />
      <AccessibilityWidget />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/identity-upload/:token" element={<IdentityUpload />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/destinations" element={<Destinations />} />
        <Route path="/destination/:code" element={<DestinationDetail />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/account" element={<Account />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/business" element={<Business />} />
        <Route path="/compatibility" element={<Compatibility />} />
        <Route path="/sims" element={<SimsConsole />} />
        <Route path="/app" element={<AppDownload />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/r/:code" element={<ReferralLanding />} />
        <Route path="/topup" element={<TopupPage />} />
        <Route path="/topup/:token" element={<TopupPage />} />
        <Route path="/legal/:page" element={<LegalPage />} />
        <Route path="/carta-servizi" element={<ServiceCharter />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
  );
}

/** Banner fluttuante durante l'impersonazione: mostra l'utenza attiva (con flag dealer)
 *  e permette all'admin di tornare alla propria sessione con un clic. */
function ImpersonationBanner() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [active] = useState(() => {
    try { return !!localStorage.getItem("atlas_impersonator"); } catch { return false; }
  });
  if (!active || !user) return null;
  const back = () => {
    try {
      const prev = localStorage.getItem("atlas_impersonator");
      localStorage.removeItem("atlas_impersonator");
      if (prev) localStorage.setItem("atlas_token", prev);
    } catch { /* storage non disponibile */ }
    window.location.href = "/admin";
  };
  return (
    <div data-testid="impersonation-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-3 rounded-full bg-amber-500 text-white pl-5 pr-2 py-2 shadow-xl shadow-amber-900/30">
      <span className="font-mono-t text-[10px] font-bold uppercase tracking-widest">
        {t("imp.banner")} {user.email}{user.is_dealer ? " · dealer" : ""}
      </span>
      <button data-testid="impersonation-back" onClick={back}
        className="rounded-full bg-white text-amber-600 px-4 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-amber-50 transition-colors">
        {t("imp.back")}
      </button>
    </div>
  );
}

function App() {
  const [googleClientId, setGoogleClientId] = useState("");
  useEffect(() => {
    api.get("/config").then(({ data }) => setGoogleClientId(data.google_client_id || "")).catch(() => {});
  }, []);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const tree = (
    <BrowserRouter>
      <AppRouter />
      <ImpersonationBanner />
      {googleClientId && <GoogleOneTap />}
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );

  return (
    <LanguageProvider>
      <AuthProvider>
        {googleClientId
          ? <GoogleOAuthProvider clientId={googleClientId}>{tree}</GoogleOAuthProvider>
          : tree}
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
